import { useEffect, useMemo, useState } from 'react'

type ResponsiveBreakpoint<T extends Record<string, any>> = {
  maxWidth: number
} & T

// Overload for when no parameters are passed - returns only windowWidth
export function useResponsive(): { windowWidth: number }

// Overload for when breakpoints and fallback are passed - returns responsive data
export function useResponsive<T extends Record<string, any>>(
  breakpoints: ResponsiveBreakpoint<T>[],
  fallbackValue: T,
  onBreakpointChange?: (breakpoint: ResponsiveBreakpoint<T>, windowWidth: number) => void
): T & { windowWidth: number }

export function useResponsive<T extends Record<string, any>>(
  breakpoints?: ResponsiveBreakpoint<T>[],
  fallbackValue?: T,
  onBreakpointChange?: (breakpoint: ResponsiveBreakpoint<T>, windowWidth: number) => void
) {
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0)

  // Find current breakpoint data - only when breakpoints are provided
  const currentData = useMemo(() => {
    if (!breakpoints || !fallbackValue) return {}

    const matchingBreakpoint = breakpoints.find((bp) => windowWidth <= bp.maxWidth)
    if (!matchingBreakpoint) return fallbackValue

    const { maxWidth, ...data } = matchingBreakpoint
    return data as any as T
  }, [windowWidth, breakpoints, fallbackValue])

  // Handle window resize
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      const newWidth = window.innerWidth
      setWindowWidth(newWidth)

      // Call callback if provided
      if (onBreakpointChange && breakpoints) {
        const newBreakpoint = breakpoints.find((bp) => newWidth <= bp.maxWidth)
        if (newBreakpoint) {
          onBreakpointChange(newBreakpoint, newWidth)
        }
      }
    }

    window.addEventListener('resize', handleResize)
    // Initial call
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [breakpoints, onBreakpointChange])

  return { ...currentData, windowWidth }
}
